import Api from "@/api";
import * as Billing from "@/models/billing";
import * as Pages from "@/components/Pages";
import * as Paper from "@/components/PaperContainer";
import * as React from "react";

import { OperatelyLogo } from "@/components/OperatelyLogo";

import { SignInWithGoogleButton } from "@/features/auth/Buttons";
import { logIn } from "@/routes/auth";
import { Paths } from "@/routes/paths";
import { PageModule } from "@/routes/types";
import classNames from "classnames";
import { Forms, DimmedLink, Link, type FormState } from "turboui";

export default { name: "LoginPage", loader: Pages.emptyLoader, Page } as PageModule;

function Page() {
  const [error, setError] = React.useState<string | null>(null);
  const inviteToken = React.useMemo(() => new URLSearchParams(window.location.search).get("invite_token"), []);
  const redirectTo = React.useMemo(() => getRedirectTo(), []);

  const form = Forms.useForm({
    fields: {
      email: "",
      password: "",
    },
    submit: async () => {
      setError(null);

      const res = await logIn(form.values.email, form.values.password, {
        redirectTo: inviteToken ? null : redirectTo,
        skipRedirect: Boolean(inviteToken),
      });

      if (res === "failure") {
        setError("Invalid email or password");
        return;
      }

      if (inviteToken) {
        try {
          const joinResult = await Api.invitations.joinCompanyViaInviteLink({ token: inviteToken });
          const companyId = joinResult.company?.id;

          if (companyId) {
            window.location.href = `/${companyId}`;
          } else {
            window.location.href = "/";
          }
        } catch (err) {
          console.error("Failed to join company via invite link after login", err);

          if (Billing.extractLimitError(err)?.code === "member_count_limit_exceeded") {
            window.location.href = Paths.inviteJoinFullPath(inviteToken);
            return;
          }

          setError("Something went wrong while joining. Please try again.");
        }
      }
    },
  });

  return (
    <Pages.Page title={["Sign In"]} testId="login-page">
      <Paper.Root size="tiny">
        <Paper.Body className="h-dvh sm:h-auto">
          <div className="py-8 sm:px-4 sm:py-4">
            <OperatelyLogo width="40px" height="40px" />
            <h1 className="text-2xl font-bold mt-4">Operately</h1>
            <p className="text-content-dimmed mb-8">Введи свои данные для входа</p>

            <Forms.Form form={form}>
              {window.appConfig.allowLoginWithEmail && <EmailLogin form={form} error={error} />}
              {window.appConfig.allowLoginWithGoogle && <GoogleLogin />}

              {isSignupEnabled() && (
                <div className="mt-8 text-center text-sm font-medium">Нет аккаунта?<Link to="/sign_up">Создать аккаунт</Link>
                </div>
              )}
            </Forms.Form>
          </div>
        </Paper.Body>
      </Paper.Root>
    </Pages.Page>
  );
}

function isSignupEnabled(): boolean {
  return window.appConfig.allowSignupWithEmail || window.appConfig.allowSignupWithGoogle;
}

function EmailLogin({ form, error }: { form: FormState<{ email: string; password: string }>; error: string | null }) {
  return (
    <div>
      <Forms.FieldGroup>
        <Forms.TextInput field={"email"} label="Электронная почта" placeholder="your@email.com" required />
        <PasswordInput />
      </Forms.FieldGroup>

      {error && <div className="text-red-600 text-sm mt-2">{error}</div>}

      <div className="mt-8">
        <SubmitButton onClick={form.actions.submit} />
      </div>
    </div>
  );
}

function PasswordInput() {
  return (
    <Forms.PasswordInput
      field={"password"}
      label={
        <div className="flex justify-between w-full">
          <span>Пароль</span>
          <ForgotPasswordLink />
        </div>
      }
      placeholder="Пароль"
      required
    />
  );
}

function ForgotPasswordLink() {
  return (
    <DimmedLink to={Paths.forgotPasswordPath()} className="text-sm font-normal" testId="forgot-password-link">Забыли пароль?</DimmedLink>
  );
}

function GoogleLogin() {
  return (
    <div>
      {window.appConfig.allowLoginWithEmail && <OrSeparator />}
      <SignInWithGoogleButton />
    </div>
  );
}

function SubmitButton({ onClick }: { onClick: () => void }) {
  const className = classNames(
    "w-full flex justify-center py-2 px-4",
    "border border-transparent",
    "rounded-md shadow-sm font-medium text-white-1",
    "bg-blue-600 hover:bg-blue-700",
  );

  return (
    <button type="submit" className={className} onClick={onClick} data-test-id="submit">Войти</button>
  );
}

function OrSeparator() {
  return (
    <div className="flex items-center gap-4 my-6 text-content-dimmed uppercase text-xs font-medium tracking-wide">
      <div className="border-t border-stroke-base flex-1" />или<div className="border-t border-stroke-base flex-1" />
    </div>
  );
}

function getRedirectTo(): string | null {
  const query = new URLSearchParams(window.location.search);
  const redirectTo = query.get("redirect_to");

  if (redirectTo) {
    const decoded = decodeURIComponent(redirectTo);

    return decoded.startsWith("/") ? decoded : null;
  } else {
    return null;
  }
}
